import { RequestConfig } from 'utils/AxiosClient';
import chatttSDK from 'services/chattt';
import { SystemLanguage } from 'hooks/business/system/useSystem';
import productLine from 'productLine';
import { flagrClient } from './leyanBotHost';
import { noop } from 'lodash-es';
import { NIMGetInstanceOptions } from '@yxim/nim-web-sdk/dist/SDK/NIM_Web_NIM/types';

export enum FlagKey {
  Config = 'kyyp85q9wsgegzjbc',
  KageEnable = 'kn61ubyeuopzwdfm4',
  RefundEnable = 'kmnuap5k4161ztpnb',
  ReviewEnable = 'kihrqunhk925tbuwe',
  QaItemEnable = 'kf5jq31zax3y3557p',
  ChannelModules = 'kw4t3eed7ec4n18eu',
  WelfareCenter = 'kpr8ovmkhk76eycsg',
  Anniversary = 'k8c9i3i5jpwkyi6ng',
  DashboardPerfEnable = 'kv5r7s16zezjisup8',
  FeedbackCaseCategoryList = 'kf3irnxcrihi6d1fp',
  DashboardRankEnable = 'k3eybxn2az5t5xvyt',
  NimVersion = 'k7opgt9titjsq6rjj',
  AuthShop = 'k9c8xjbvccrp3yqr3',
  XiapibuyEnable = 'ksg8dsxn7ddkc4gn5',
  ShopeeImageDomainScore = 'ko7j9ie2fvue1mh4r',
  ScoreEnable = 'k1yzbdwigyzhcrirx',
  NotifyInterval = 'k6g96uvidfrb4y3jc',
  PaySupport = 'ky99ioceg7o7rz37t',
  ServerEnable = 'khras7zeam8tcsv1e',
  HostList = 'kfakjae6fjubjzvo8',
  TiktokImageProxyOriginList = 'kdi1b4os463o7r2fi',
  MercadoSyncMessageSeconds = 'ku8rodgpjz4qzvy9b',
  ConversationLruLimit = 'k4t9dx6h1sf9o9tgs',
}

export interface FlagrResponse<T extends Object> {
  variantAttachment: T;
}

export interface FlagrBatchResponse<T extends Object> {
  evaluationResults: Array<{
    evalContext: {
      entityContext: {
        _targetKey: string;
      };
      flagKey: string;
    };
    flagKey: string;
    variantAttachment: T;
  }>;
}

export interface ConfigResponse {
  // 为了避免轮训的时候setState 引起重渲染，在这里增加个version，只有当轮训查到的版本和当前版本不一致时再setState
  version: string;
  contactQrcode: string;
  payQrcode: string;
  billQrcode: string;
  qaQrcode: string;
  qaRegionList: General.Region[];
  // 禁用授权代理org
  authProxyDisabledList: number[];
  latestVersion: string;
  newOrgSyncMessageIntervalSeconds: number;
  androidQrcode: string;
  iosQrcode: string;
  // 超时自动回复延迟时间最小值
  autoReplyDelayMinLimit: number;
  // 超时自动回复延迟时间最大值
  autoReplyDelayMaxLimit: number;
  // 超时自动回复冷却时间最小值
  autoReplyFrequencyMinLimit: number;
  // 超时自动回复冷却时间最大值
  autoReplyFrequencyMaxLimit: number;
  // 批量转接会话上限
  batchTransferMaxLimit: number;
  // 后端服务搜索
  onlineSearch: boolean;
  // 耗时超过一定毫秒数的请求才上报
  requestReportMinDuration: number;
  // 云信 db 开关
  nimDb: boolean;
  // 云信 dbLog 开关
  nimDbLog: boolean;
  // 云信 debug 开关
  nimLogLevel?: NIMGetInstanceOptions['logLevel'];
  // 上报请求耗时 URL 名单
  requestDurationURL: string[];
  // 活动弹窗在距离结束前多少天进行提示
  subscribeModalWaringDaysBeforeExpired: number;
  // Tokopedia 联系客服二维码
  tokopediaContactQrcode: string;
  // 联系方式
  contactNumber: string;
}

export interface ConfigParams {
  orgId: number;
}

export interface DashboardPerfParams {
  orgId: number;
}

export interface EnableResponse {
  enable: true;
}

export interface ShopeeImageDomainScoreResponse {
  enable: true;
  reportAllEnable?: true;
}

export interface ChannelModulesResponse {
  /**
   * 知虾相关的渠道组件的渲染开关
   */
  zhixia: {
    /**
     * 应用中心：知虾卡片
     */
    appCenterCard: boolean;
    /**
     * 左边导航栏 知虾按钮
     */
    nav: boolean;
    /**
     * 不设置默认为true，为false时不允许访问 '/zhixia' 路由(注意nav跟应用卡片对该路由有依赖)
     */
    route: boolean;
    /**
     * 顶栏的 知虾动态icon(zhixialogo.gif)
     */
    topbarGif: boolean;
    /**
     * 订购页面 知虾的优惠推广信息(zhixiaGift 组件)
     */
    zhixiaGift: boolean;
  };
}

export interface WelfareCenterConfig {
  iconUrl: string;
  text: Record<SystemLanguage, string>;
  text2: Record<SystemLanguage, string>;
  redirectPath: string;
}

export interface WelfareCenterResponse {
  // 订购中心-福利中心滚动条控制开关
  marketWelfareBar?: boolean;
  welfareCenterConfig: WelfareCenterConfig[];
}

export interface AnniversaryResponse {
  skipModal?: boolean;
}

export interface DashboardPerfResponse {
  allowed?: boolean;
  perfNav?: boolean;
}

export interface FeedbackCaseCategoryListResponse {
  list: Feedback.CaseCategory[];
}

export interface NimVersionResponse {
  version: string;
}

export type AuthShopOpenMode = 'internal' | 'external';

export interface AuthShopResponse {
  // 授权打开浏览器方式
  openMode: AuthShopOpenMode;
}

export interface HostListResponse {
  leyanbot: Array<{ domain: string; delay?: number }>;
  flagr: Array<{ domain: string }>;
  version: number;
}

export interface Response {
  [FlagKey.Config]: ConfigResponse;
  [FlagKey.KageEnable]: EnableResponse;
  [FlagKey.RefundEnable]: EnableResponse;
  [FlagKey.ReviewEnable]: EnableResponse;
  [FlagKey.QaItemEnable]: EnableResponse;
  [FlagKey.ChannelModules]: ChannelModulesResponse;
  [FlagKey.WelfareCenter]: WelfareCenterResponse;
  [FlagKey.Anniversary]: AnniversaryResponse;
  [FlagKey.DashboardPerfEnable]: DashboardPerfResponse;
  [FlagKey.FeedbackCaseCategoryList]: FeedbackCaseCategoryListResponse;
  [FlagKey.DashboardRankEnable]: {
    allowed?: boolean;
  };
  [FlagKey.NimVersion]: NimVersionResponse;
  [FlagKey.AuthShop]: AuthShopResponse;
  [FlagKey.XiapibuyEnable]: EnableResponse;
  [FlagKey.ShopeeImageDomainScore]: ShopeeImageDomainScoreResponse;
  [FlagKey.ScoreEnable]: EnableResponse;
  [FlagKey.NotifyInterval]: { interval?: number };
  [FlagKey.PaySupport]: Record<string, string[]>;
  [FlagKey.ServerEnable]: EnableResponse;
  [FlagKey.HostList]: HostListResponse;
  [FlagKey.TiktokImageProxyOriginList]: { originList: string[] };
  [FlagKey.MercadoSyncMessageSeconds]: { seconds: number };
  [FlagKey.ConversationLruLimit]: { limit: number };
}

export interface EnableParams {
  orgId: number;
}

export interface NimVersionParams {
  orgId: number;
  userId: number;
  countryCode?: string;
}

export interface XiapibuyEnableParams {
  orgId: number;
  userId: number;
  countryCode?: string;
}

export interface UserParams {
  orgId: number;
  userId: number;
}

export interface Params {
  [FlagKey.Config]: ConfigParams;
  [FlagKey.KageEnable]: EnableParams;
  [FlagKey.RefundEnable]: EnableParams;
  [FlagKey.ReviewEnable]: EnableParams;
  [FlagKey.QaItemEnable]: EnableParams;
  [FlagKey.ChannelModules]: ConfigParams;
  [FlagKey.WelfareCenter]: ConfigParams;
  [FlagKey.Anniversary]: ConfigParams;
  [FlagKey.DashboardPerfEnable]: DashboardPerfParams;
  [FlagKey.FeedbackCaseCategoryList]: any;
  [FlagKey.DashboardRankEnable]: ConfigParams;
  [FlagKey.NimVersion]: NimVersionParams;
  [FlagKey.AuthShop]: ConfigParams;
  [FlagKey.XiapibuyEnable]: XiapibuyEnableParams;
  [FlagKey.ShopeeImageDomainScore]: UserParams;
  [FlagKey.ScoreEnable]: EnableParams;
  [FlagKey.NotifyInterval]: UserParams;
  [FlagKey.PaySupport]: EnableParams;
  [FlagKey.TiktokImageProxyOriginList]: UserParams;
  [FlagKey.MercadoSyncMessageSeconds]: ConfigParams;
  [FlagKey.ConversationLruLimit]: UserParams;
  [FlagKey.ServerEnable]: any;
  [FlagKey.HostList]: any;
}

/**
 *
 * @param key
 * @param params 请求时默认自动携带当前渠道信息
 * @param config
 * @returns {Promise<Response[T]|undefined>} 返回对应flag的配置，失败时返回undefined
 */
function fetchFlag<T extends keyof Params>(key: T, params: Params[T], config: RequestConfig = {}) {
  return flagrClient
    .post<FlagrResponse<Response[T]>>(
      '/evaluation',
      {
        entityContext: {
          ...params,
          // 默认携带 channel, 空字符串认为是浏览器
          channel: chatttSDK?.app?.channel || '',
          productLine,
        },
        flagKey: key,
      },
      config,
    )
    .then((res) => {
      const {
        data: { variantAttachment },
      } = res;
      return variantAttachment;
    })
    .catch(noop);
}

// 批量获取 flagr 配置
export function fetchFlagBatch<T extends keyof Params>(body: Array<[T, Params[T]]>) {
  const bodyData = body.reduce(
    (prev, [key, params]) => {
      prev.flagKeys.push(key);
      prev.entities.push({
        entityContext: {
          ...params,
          // 默认携带 channel, 空字符串认为是浏览器
          channel: chatttSDK?.app?.channel || '',
          productLine,
          _targetKey: key,
        },
      });
      return prev;
    },
    { entities: [], flagKeys: [] } as { entities: Array<Params[T]>; flagKeys: Array<T> },
  );
  return flagrClient
    .post<FlagrBatchResponse<Response[T]>>('/evaluation/batch', bodyData)
    .then(({ data }) => {
      const result = data.evaluationResults?.filter(({ evalContext }) => {
        return evalContext.flagKey === evalContext.entityContext._targetKey;
      });
      return bodyData.flagKeys.map((flagKey) => {
        const r = result.find((item) => item.flagKey === flagKey);
        return r?.variantAttachment;
      });
    })
    .catch(noop);
}

export function configFlag(orgId: number) {
  return fetchFlag(FlagKey.Config, { orgId });
}

export function kageEnableFlag(orgId: number) {
  return fetchFlag(FlagKey.KageEnable, { orgId });
}

export function refundEnableFlag(orgId: number) {
  return fetchFlag(FlagKey.RefundEnable, { orgId });
}

export function reviewEnableFlag(orgId: number) {
  return fetchFlag(FlagKey.ReviewEnable, { orgId });
}

export function navFlag(orgId: number) {
  return fetchFlag(FlagKey.ChannelModules, { orgId });
}

export function anniversaryFlag(orgId: number) {
  return fetchFlag(FlagKey.Anniversary, { orgId });
}

export function dashboardPrefFlag(orgId: number) {
  return fetchFlag(FlagKey.DashboardPerfEnable, { orgId });
}

export function dashboardRankFlag(orgId: number) {
  return fetchFlag(FlagKey.DashboardRankEnable, { orgId });
}

export function feedbackCaseCategoryListFlag() {
  return fetchFlag(FlagKey.FeedbackCaseCategoryList, undefined);
}

export function nimVersionFlag(params: NimVersionParams) {
  return fetchFlag(FlagKey.NimVersion, {
    ...params,
    countryCode: chatttSDK?.app?.getLocaleCountryCode() || 'CN',
  });
}

export function authShopFlag(orgId: number) {
  return fetchFlag(FlagKey.AuthShop, { orgId });
}

export function xiapibuyEnableFlag(params: XiapibuyEnableParams) {
  return fetchFlag(FlagKey.XiapibuyEnable, {
    ...params,
    countryCode: chatttSDK?.app?.getLocaleCountryCode() || 'CN',
  });
}

export function shopeeImageDomainScoreFlag(params: UserParams) {
  return fetchFlag(FlagKey.ShopeeImageDomainScore, params);
}

export function scoreEnableFlag(orgId: number) {
  return fetchFlag(FlagKey.ScoreEnable, { orgId });
}

export function serverEnableFlag() {
  return fetchFlag(FlagKey.ServerEnable, undefined);
}

export function hostListFlag() {
  return fetchFlag(FlagKey.HostList, undefined, { timeout: 1000 });
}

export default fetchFlag;
